import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

export const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name || !email || !password || !confirmPassword) {
      toast.error('Por favor, preencha todos os campos');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    if (password.length < 6) {
      toast.error('A senha deve ter no mínimo 6 caracteres');
      return;
    }

    setLoading(true);
    try {
      const user = await register(name, email, password);
      toast.success(`Bem-vindo, ${user.name}!`);
      navigate('/');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao criar conta';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#E9E5D6] flex items-center justify-center px-4" data-testid="register-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <span className="font-['Playfair_Display'] text-3xl font-bold text-[#0F5A34]">
              Portugal
            </span>
            <span className="font-['Playfair_Display'] text-3xl font-bold text-[#E53935] ml-1">
              à Vista
            </span>
          </Link>
        </div>

        {/* Form Card */}
        <div className="bg-white p-8 rounded-sm border border-[#D6D2C4] shadow-sm">
          <h1 className="font-['Playfair_Display'] text-2xl font-bold text-[#1A1A1A] mb-2 text-center">
            Criar Conta
          </h1>
          <p className="text-[#5C5C5C] font-['Inter'] text-sm text-center mb-6">
            Junte-se à comunidade Portugal à Vista
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="font-['Montserrat'] text-sm font-medium">
                Nome
              </Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Seu nome"
                required
                className="border-[#D6D2C4]"
                data-testid="register-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="font-['Montserrat'] text-sm font-medium">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="seu@email.com"
                required
                className="border-[#D6D2C4]"
                data-testid="register-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="font-['Montserrat'] text-sm font-medium">
                Senha
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  required
                  className="border-[#D6D2C4] pr-10"
                  data-testid="register-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5C5C5C] hover:text-[#1A1A1A]"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="font-['Montserrat'] text-sm font-medium">
                Confirmar Senha
              </Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repita a senha"
                required
                className="border-[#D6D2C4]"
                data-testid="register-confirm-password"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#0F5A34] hover:bg-[#0A3F24] font-['Montserrat'] text-sm"
              data-testid="register-submit"
            >
              {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
              Criar Conta
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-[#5C5C5C] font-['Inter'] text-sm">
              Já tem uma conta?{' '}
              <Link to="/login" className="text-[#0F5A34] font-medium hover:underline">
                Entrar
              </Link>
            </p>
          </div>
        </div>

        {/* Back to home */}
        <div className="text-center mt-6">
          <Link to="/" className="text-[#5C5C5C] font-['Inter'] text-sm hover:text-[#0F5A34]">
            ← Voltar ao início
          </Link>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
